export const KpiPeriod = {
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
} as const;

export type KpiPeriod = Capitalize<Extract<KpiConfigPeriod, 'month' | 'quarter' | 'year'>>;
export function isKpiPeriod(period: string): period is KpiPeriod {
  return Object.values(KpiPeriod).includes(period as KpiPeriod);
}

export type KpiPeriodExtended =
  | Extract<KpiConfigPeriod, 'CYTD' | 'LTM'>
  | Capitalize<Extract<KpiConfigPeriod, 'growthMonth' | 'growthYear' | 'growthQuarter'>>;

export const KpiSection = {
  actual: 'actual',
  budget: 'budget',
} as const;
export type KpiSection = (typeof KpiSection)[keyof typeof KpiSection];

export interface ICompanyFinancialsDataModel {
  calculated?: boolean;
  companyId: number;
  createdAt: string;
  // Reported (native) currency of the stored value (read). Write uses currencyCode.
  fx_currency?: string | null;
  currencyCode?: string;
  // BE could not resolve an FX rate to the requested currency; value is unconverted.
  currencyUnresolved?: boolean;
  date: string;
  iLevelId?: number;
  id: number;
  // kpiId should correspond to IField.id
  kpiId: number;
  period: KpiPeriod | KpiPeriodExtended;
  section: KpiSection;
  // Provenance of the resolved value (read-only); `source` is the API's KpiCustomDataSource, `user` for typed values.
  source?: string | null;
  sourceType?: string | null;
  sourceDocumentType?: string | null;
  // How many source rows back this cell and how many distinct values they hold; absent on derived rows.
  sourceCount?: number;
  distinctSourceValueCount?: number;
  // A person pinned one of the sources as the cell's value (FD-5414).
  hasPinnedSource?: boolean;
  // Whether review has settled the value (`provisional`, `confirmed`, `reviewed`; open); absent from older APIs.
  reviewState?: string | null;
  updatedAt: string;
  value: string;
}

export function createCompanyFinancialsDataModel(
  overrides: Partial<ICompanyFinancialsDataModel> = {}
): ICompanyFinancialsDataModel {
  return {
    calculated: false,
    companyId: 0,
    createdAt: '',
    fx_currency: null,
    date: '',
    iLevelId: 0,
    id: 0,
    kpiId: 0,
    period: KpiPeriod.quarter,
    section: KpiSection.actual,
    updatedAt: '',
    value: '',
    ...overrides,
  };
}
export const KpiConfigPeriod = {
  GrowthMonth: 'growthMonth',
  GrowthQuarter: 'growthQuarter',
  GrowthYear: 'growthYear',
  Latest: 'latest',
  Month: 'month',
  Quarter: 'quarter',
  Year: 'year',
  CYTD: 'CYTD',
  LTM: 'LTM',
} as const;

export type KpiConfigPeriod = (typeof KpiConfigPeriod)[keyof typeof KpiConfigPeriod];

export const KpiConfigPeriodToName: Record<KpiConfigPeriod, string> = {
  [KpiConfigPeriod.GrowthMonth]: 'MoM Change',
  [KpiConfigPeriod.GrowthQuarter]: 'QoQ Change',
  [KpiConfigPeriod.GrowthYear]: 'YoY Change',
  [KpiConfigPeriod.Latest]: 'Latest',
  [KpiConfigPeriod.Month]: 'Month',
  [KpiConfigPeriod.Quarter]: 'Quarter',
  [KpiConfigPeriod.Year]: 'Year',
  [KpiConfigPeriod.CYTD]: 'CYTD',
  [KpiConfigPeriod.LTM]: 'LTM',
};
